import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type FieldValue,
  type Unsubscribe,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import { canvasWritesFromDraft, type CanvasGraphWrites } from './peopleTravelCanvasApply';
import { buildTripCanvasSeed } from './peopleTravelCanvasSeed';
import { buildStopsFromTripPlanBoard } from './peopleTravelCanvasStops';
import type { TripPlanDraft } from './peopleTravelPlanService';
import type {
  PeopleTravelBudget,
  PeopleTravelEvent,
  PeopleTravelEventKind,
  PeopleTravelLeg,
  PeopleTravelLegMode,
  PeopleTravelLocation,
  PeopleTravelOption,
  PeopleTravelOptionKind,
  PeopleTravelPlannerMessage,
  PeopleTravelStop,
  PeopleTravelStopKind,
  PeopleTravelTrip,
  PeopleTravelTripPurpose,
  PeopleTravelTripStatus,
} from '../types';

export const PEOPLE_TRAVEL_TRIPS = 'peopleTravelTrips';
export const PEOPLE_TRAVEL_SEARCH_REQUESTS = 'peopleTravelSearchRequests';
export const PEOPLE_TRAVEL_CALENDAR_SYNC_REQUESTS = 'peopleTravelCalendarSyncRequests';

function parseDate(value: unknown): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function defaultBudget(): PeopleTravelBudget {
  return {
    currency: 'USD',
    estimatedTotal: 0,
    selectedTotal: 0,
  };
}

export function parsePeopleTravelTrip(id: string, raw: Record<string, unknown>): PeopleTravelTrip {
  const budgetRaw = (raw.budget as Record<string, unknown>) || {};
  return {
    id,
    title: String(raw.title || 'Untitled trip'),
    purpose: raw.purpose as PeopleTravelTripPurpose | undefined,
    ownerUserId: String(raw.ownerUserId || ''),
    travelerUserIds: Array.isArray(raw.travelerUserIds)
      ? raw.travelerUserIds.map(String)
      : [],
    startDate: String(raw.startDate || ''),
    endDate: String(raw.endDate || ''),
    destinations: Array.isArray(raw.destinations)
      ? (raw.destinations as PeopleTravelTrip['destinations'])
      : [],
    status: (raw.status as PeopleTravelTripStatus) || 'draft',
    budget: {
      currency: 'USD',
      cap: typeof budgetRaw.cap === 'number' ? budgetRaw.cap : undefined,
      estimatedTotal: typeof budgetRaw.estimatedTotal === 'number' ? budgetRaw.estimatedTotal : 0,
      selectedTotal: typeof budgetRaw.selectedTotal === 'number' ? budgetRaw.selectedTotal : 0,
      actualSpent: typeof budgetRaw.actualSpent === 'number' ? budgetRaw.actualSpent : undefined,
    },
    itineraryNotes: typeof raw.itineraryNotes === 'string' ? raw.itineraryNotes : undefined,
    calendarExecutiveUid:
      typeof raw.calendarExecutiveUid === 'string' ? raw.calendarExecutiveUid : undefined,
    calendarInvitees: Array.isArray(raw.calendarInvitees)
      ? raw.calendarInvitees.map(String)
      : undefined,
    calendarEventGroupId:
      typeof raw.calendarEventGroupId === 'string' ? raw.calendarEventGroupId : undefined,
    calendarSyncStatus: raw.calendarSyncStatus as PeopleTravelTrip['calendarSyncStatus'],
    calendarSyncedAt: raw.calendarSyncedAt ? parseDate(raw.calendarSyncedAt) : undefined,
    calendarSyncError:
      typeof raw.calendarSyncError === 'string' ? raw.calendarSyncError : undefined,
    createdAt: parseDate(raw.createdAt),
    updatedAt: parseDate(raw.updatedAt),
    createdByUserId: String(raw.createdByUserId || raw.ownerUserId || ''),
  };
}

export function parsePeopleTravelOption(id: string, raw: Record<string, unknown>): PeopleTravelOption {
  return {
    id,
    kind: (raw.kind as PeopleTravelOptionKind) || 'flight',
    source: raw.source === 'agent' ? 'agent' : 'manual',
    label: String(raw.label || 'Option'),
    vendor: typeof raw.vendor === 'string' ? raw.vendor : undefined,
    price: typeof raw.price === 'number' ? raw.price : 0,
    currency: 'USD',
    details: (raw.details as Record<string, unknown>) || {},
    deepLink: typeof raw.deepLink === 'string' ? raw.deepLink : undefined,
    rank: typeof raw.rank === 'number' ? raw.rank : undefined,
    isSelected: raw.isSelected === true,
    isBooked: raw.isBooked === true,
    searchRequestId:
      typeof raw.searchRequestId === 'string' ? raw.searchRequestId : undefined,
    createdAt: parseDate(raw.createdAt),
    legId: typeof raw.legId === 'string' ? raw.legId : undefined,
    eventId: typeof raw.eventId === 'string' ? raw.eventId : undefined,
  };
}

export function parsePeopleTravelLocation(
  id: string,
  raw: Record<string, unknown>,
): PeopleTravelLocation {
  return {
    id,
    label: String(raw.label || 'Location'),
    kind: typeof raw.kind === 'string' ? raw.kind : undefined,
    address: typeof raw.address === 'string' ? raw.address : undefined,
    placeId: typeof raw.placeId === 'string' ? raw.placeId : undefined,
    lat: typeof raw.lat === 'number' ? raw.lat : undefined,
    lng: typeof raw.lng === 'number' ? raw.lng : undefined,
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 0,
    x: typeof raw.x === 'number' ? raw.x : 80,
    y: typeof raw.y === 'number' ? raw.y : 80,
    startDate: typeof raw.startDate === 'string' ? raw.startDate : undefined,
    endDate: typeof raw.endDate === 'string' ? raw.endDate : undefined,
  };
}

export function parsePeopleTravelEvent(
  id: string,
  raw: Record<string, unknown>,
): PeopleTravelEvent {
  const kind = String(raw.kind || 'other');
  const kinds: PeopleTravelEventKind[] = ['work', 'meeting', 'hotel', 'meal', 'other'];
  return {
    id,
    locationId: String(raw.locationId || ''),
    title: String(raw.title || 'Event'),
    kind: kinds.includes(kind as PeopleTravelEventKind)
      ? (kind as PeopleTravelEventKind)
      : 'other',
    startAt: typeof raw.startAt === 'string' ? raw.startAt : undefined,
    endAt: typeof raw.endAt === 'string' ? raw.endAt : undefined,
    notes: typeof raw.notes === 'string' ? raw.notes : undefined,
    x: typeof raw.x === 'number' ? raw.x : 16,
    y: typeof raw.y === 'number' ? raw.y : 48,
  };
}

export function parsePeopleTravelLeg(id: string, raw: Record<string, unknown>): PeopleTravelLeg {
  const mode = String(raw.mode || 'other');
  const modes: PeopleTravelLegMode[] = ['flight', 'drive', 'rental_car', 'other'];
  return {
    id,
    fromLocationId: String(raw.fromLocationId || ''),
    toLocationId: String(raw.toLocationId || ''),
    mode: modes.includes(mode as PeopleTravelLegMode) ? (mode as PeopleTravelLegMode) : 'other',
    travelerUserIds: Array.isArray(raw.travelerUserIds)
      ? raw.travelerUserIds.map(String)
      : undefined,
    notes: typeof raw.notes === 'string' ? raw.notes : undefined,
    selectedOptionIds: Array.isArray(raw.selectedOptionIds)
      ? raw.selectedOptionIds.map(String)
      : undefined,
  };
}

export function parsePeopleTravelStop(id: string, raw: Record<string, unknown>): PeopleTravelStop {
  return {
    id,
    kind: (raw.kind as PeopleTravelStopKind) || 'other',
    label: String(raw.label || 'Stop'),
    address: typeof raw.address === 'string' ? raw.address : undefined,
    lat: typeof raw.lat === 'number' ? raw.lat : undefined,
    lng: typeof raw.lng === 'number' ? raw.lng : undefined,
    startAt: typeof raw.startAt === 'string' ? raw.startAt : undefined,
    endAt: typeof raw.endAt === 'string' ? raw.endAt : undefined,
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 0,
    linkedOptionId:
      typeof raw.linkedOptionId === 'string' ? raw.linkedOptionId : undefined,
    source:
      raw.source === 'plan_draft' || raw.source === 'agent' || raw.source === 'manual'
        ? raw.source
        : undefined,
    boardKind:
      raw.boardKind === 'location' || raw.boardKind === 'event' || raw.boardKind === 'leg'
        ? raw.boardKind
        : undefined,
    boardId: typeof raw.boardId === 'string' ? raw.boardId : undefined,
  };
}

export function computeBudgetRollup(options: PeopleTravelOption[]): Pick<
  PeopleTravelBudget,
  'estimatedTotal' | 'selectedTotal'
> {
  const agentByKind = new Map<PeopleTravelOptionKind, PeopleTravelOption[]>();
  for (const opt of options) {
    const list = agentByKind.get(opt.kind) || [];
    list.push(opt);
    agentByKind.set(opt.kind, list);
  }

  let estimatedTotal = 0;
  agentByKind.forEach(list => {
    const sorted = [...list].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    const best = sorted[0];
    if (best && best.price > 0) estimatedTotal += best.price;
  });

  let selectedTotal = 0;
  for (const opt of options) {
    if (opt.isSelected && opt.price > 0) selectedTotal += opt.price;
  }

  return { estimatedTotal, selectedTotal };
}

export async function recomputeTripBudget(tripId: string): Promise<void> {
  const optionsSnap = await getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options'));
  const options = optionsSnap.docs.map(d => parsePeopleTravelOption(d.id, d.data()));
  const rollup = computeBudgetRollup(options);
  await updateDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId), {
    'budget.estimatedTotal': rollup.estimatedTotal,
    'budget.selectedTotal': rollup.selectedTotal,
    updatedAt: serverTimestamp(),
  });
}

export function subscribePeopleTravelTrips(args: {
  viewerUid: string;
  orgWide: boolean;
  onData: (trips: PeopleTravelTrip[]) => void;
  onError?: (e: Error) => void;
}): Unsubscribe {
  const col = collection(db, PEOPLE_TRAVEL_TRIPS);
  const q = args.orgWide
    ? query(col, orderBy('startDate', 'desc'))
    : query(col, where('ownerUserId', '==', args.viewerUid), orderBy('startDate', 'desc'));

  return onSnapshot(
    q,
    snap => {
      const trips = snap.docs.map(d => parsePeopleTravelTrip(d.id, d.data()));
      args.onData(trips);
    },
    err => {
      firestoreSnapshotListenError('peopleTravelFirestore', PEOPLE_TRAVEL_TRIPS, err);
      args.onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export function subscribePeopleTravelTrip(
  tripId: string,
  onData: (trip: PeopleTravelTrip | null) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, PEOPLE_TRAVEL_TRIPS, tripId),
    snap => {
      if (!snap.exists()) {
        onData(null);
        return;
      }
      onData(parsePeopleTravelTrip(snap.id, snap.data()));
    },
    err => {
      firestoreSnapshotListenError('peopleTravelFirestore', `${PEOPLE_TRAVEL_TRIPS}/${tripId}`, err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export async function createPeopleTravelTrip(input: {
  title: string;
  purpose?: PeopleTravelTripPurpose;
  ownerUserId: string;
  travelerUserIds: string[];
  startDate: string;
  endDate: string;
  destinations: PeopleTravelTrip['destinations'];
  budgetCap?: number;
  itineraryNotes?: string;
}): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in required');

  const budget = defaultBudget();
  if (typeof input.budgetCap === 'number' && input.budgetCap > 0) {
    budget.cap = input.budgetCap;
  }

  const ref = await addDoc(collection(db, PEOPLE_TRAVEL_TRIPS), {
    title: input.title.trim(),
    purpose: input.purpose || 'business',
    ownerUserId: input.ownerUserId,
    travelerUserIds: input.travelerUserIds,
    startDate: input.startDate,
    endDate: input.endDate,
    destinations: input.destinations,
    status: 'draft' as PeopleTravelTripStatus,
    budget,
    itineraryNotes: input.itineraryNotes?.trim() || '',
    calendarSyncStatus: 'none',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdByUserId: uid,
  });

  return ref.id;
}

export async function createBlankPeopleTravelTrip(ownerUserId: string): Promise<string> {
  return createPeopleTravelTrip({
    title: 'New trip',
    ownerUserId,
    travelerUserIds: [ownerUserId],
    startDate: '',
    endDate: '',
    destinations: [],
  });
}

export async function updatePeopleTravelTrip(
  tripId: string,
  patch: Partial<
    Pick<
      PeopleTravelTrip,
      | 'title'
      | 'purpose'
      | 'travelerUserIds'
      | 'startDate'
      | 'endDate'
      | 'destinations'
      | 'status'
      | 'itineraryNotes'
      | 'budget'
      | 'calendarExecutiveUid'
      | 'calendarInvitees'
      | 'calendarEventGroupId'
      | 'calendarSyncStatus'
      | 'calendarSyncError'
    >
  >,
): Promise<void> {
  type TripPatch = Partial<
    Pick<
      PeopleTravelTrip,
      | 'title'
      | 'purpose'
      | 'travelerUserIds'
      | 'startDate'
      | 'endDate'
      | 'destinations'
      | 'status'
      | 'itineraryNotes'
      | 'budget'
      | 'calendarExecutiveUid'
      | 'calendarInvitees'
      | 'calendarEventGroupId'
      | 'calendarSyncStatus'
    >
  >;
  type UpdatePayload = TripPatch & {
    updatedAt: FieldValue;
    calendarSyncedAt?: FieldValue;
    calendarSyncError?: string | null;
  };

  const payload: UpdatePayload = { updatedAt: serverTimestamp() };
  for (const [k, v] of Object.entries(patch) as [keyof typeof patch, (typeof patch)[keyof typeof patch]][]) {
    if (v !== undefined) (payload as Record<string, unknown>)[k as string] = v;
  }
  if (patch.calendarSyncStatus === 'synced') {
    payload.calendarSyncedAt = serverTimestamp();
    payload.calendarSyncError = null;
  }
  await updateDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId), payload as Record<string, FieldValue | string | number | boolean | object | null>);
}

export async function deletePeopleTravelTrip(tripId: string): Promise<void> {
  await deleteDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId));
}

export function subscribeTripOptions(
  tripId: string,
  onData: (options: PeopleTravelOption[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options'),
    snap => {
      const options = snap.docs.map(d => parsePeopleTravelOption(d.id, d.data()));
      options.sort((a, b) => {
        const rankA = a.rank ?? 999;
        const rankB = b.rank ?? 999;
        if (rankA !== rankB) return rankA - rankB;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });
      onData(options);
    },
    err => {
      firestoreSnapshotListenError('peopleTravelFirestore', `${PEOPLE_TRAVEL_TRIPS}/${tripId}/options`, err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export async function upsertTripOption(
  tripId: string,
  option: Omit<PeopleTravelOption, 'createdAt' | 'id'> & { id?: string },
): Promise<string> {
  const id = option.id || doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options')).id;
  await setDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options', id), {
    kind: option.kind,
    source: option.source,
    label: option.label,
    vendor: option.vendor || null,
    price: option.price,
    currency: 'USD',
    details: option.details || {},
    deepLink: option.deepLink || null,
    rank: option.rank ?? null,
    isSelected: option.isSelected === true,
    isBooked: option.isBooked === true,
    searchRequestId: option.searchRequestId || null,
    createdAt: serverTimestamp(),
    legId: option.legId || null,
    eventId: option.eventId || null,
  });
  await recomputeTripBudget(tripId);
  return id;
}

export async function deleteTripOption(tripId: string, optionId: string): Promise<void> {
  await deleteDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options', optionId));
  await recomputeTripBudget(tripId);
}

export async function selectTripOption(
  tripId: string,
  kind: PeopleTravelOptionKind,
  optionId: string,
): Promise<void> {
  const optionsSnap = await getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options'));
  const batch = writeBatch(db);
  for (const d of optionsSnap.docs) {
    const data = d.data();
    if (data.kind !== kind) continue;
    batch.update(d.ref, { isSelected: d.id === optionId });
  }
  await batch.commit();
  await recomputeTripBudget(tripId);
  await rebuildStopsFromSelectedOptions(tripId);
}

export function subscribeTripStops(
  tripId: string,
  onData: (stops: PeopleTravelStop[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops'),
    orderBy('sortOrder', 'asc'),
  );
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parsePeopleTravelStop(d.id, d.data())));
    },
    err => {
      firestoreSnapshotListenError('peopleTravelFirestore', `${PEOPLE_TRAVEL_TRIPS}/${tripId}/stops`, err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export async function upsertTripStop(
  tripId: string,
  stop: Omit<PeopleTravelStop, 'id'> & { id?: string },
): Promise<string> {
  const id = stop.id || doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops')).id;
  await setDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops', id), {
    kind: stop.kind,
    label: stop.label,
    address: stop.address || null,
    lat: stop.lat ?? null,
    lng: stop.lng ?? null,
    startAt: stop.startAt || null,
    endAt: stop.endAt || null,
    sortOrder: stop.sortOrder,
    linkedOptionId: stop.linkedOptionId || null,
    source: stop.source || null,
    boardKind: stop.boardKind || null,
    boardId: stop.boardId || null,
  });
  return id;
}

export async function deleteTripStop(tripId: string, stopId: string): Promise<void> {
  await deleteDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops', stopId));
}

export async function reorderTripStops(tripId: string, stopIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  stopIds.forEach((id, index) => {
    batch.update(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops', id), { sortOrder: index });
  });
  await batch.commit();
}

function stopFromOption(opt: PeopleTravelOption, sortOrder: number): Omit<PeopleTravelStop, 'id'> | null {
  const d = opt.details || {};
  const lat = typeof d.lat === 'number' ? d.lat : undefined;
  const lng = typeof d.lng === 'number' ? d.lng : undefined;
  const address = typeof d.address === 'string' ? d.address : undefined;

  if (opt.kind === 'flight') {
    const dep = typeof d.departureAirport === 'string' ? d.departureAirport : opt.label;
    const arr = typeof d.arrivalAirport === 'string' ? d.arrivalAirport : undefined;
    const stops: Omit<PeopleTravelStop, 'id'>[] = [];
    stops.push({
      kind: 'flight_departure',
      label: dep,
      address: dep,
      lat,
      lng,
      startAt: typeof d.departureAt === 'string' ? d.departureAt : undefined,
      endAt: typeof d.arrivalAt === 'string' ? d.arrivalAt : undefined,
      sortOrder,
      linkedOptionId: opt.id,
    });
    if (arr) {
      stops.push({
        kind: 'flight_arrival',
        label: arr,
        address: arr,
        sortOrder: sortOrder + 0.1,
        linkedOptionId: opt.id,
      });
    }
    return stops[0];
  }

  if (opt.kind === 'hotel') {
    return {
      kind: 'hotel',
      label: opt.label,
      address: address || opt.label,
      lat,
      lng,
      startAt: typeof d.checkInAt === 'string' ? d.checkInAt : undefined,
      endAt: typeof d.checkOutAt === 'string' ? d.checkOutAt : undefined,
      sortOrder,
      linkedOptionId: opt.id,
    };
  }

  if (opt.kind === 'rental_car') {
    return {
      kind: 'rental_pickup',
      label: opt.label,
      address: address || opt.label,
      lat,
      lng,
      startAt: typeof d.pickupAt === 'string' ? d.pickupAt : undefined,
      endAt: typeof d.dropoffAt === 'string' ? d.dropoffAt : undefined,
      sortOrder,
      linkedOptionId: opt.id,
    };
  }

  return null;
}

export async function rebuildStopsFromSelectedOptions(tripId: string): Promise<void> {
  const [tripSnap, optionsSnap, stopsSnap] = await Promise.all([
    getDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId)),
    getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options')),
    getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops')),
  ]);
  if (!tripSnap.exists()) return;

  const trip = parsePeopleTravelTrip(tripSnap.id, tripSnap.data());
  const options = optionsSnap.docs.map(d => parsePeopleTravelOption(d.id, d.data()));
  const selected = options.filter(o => o.isSelected);
  const manualStops = stopsSnap.docs
    .map(d => parsePeopleTravelStop(d.id, d.data()))
    .filter(s => !s.linkedOptionId);

  const batch = writeBatch(db);
  for (const d of stopsSnap.docs) {
    if (d.data().linkedOptionId) batch.delete(d.ref);
  }

  let order = 0;
  for (const dest of trip.destinations) {
    if (dest.lat != null && dest.lng != null) {
      const id = doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops')).id;
      batch.set(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops', id), {
        kind: order === 0 ? 'origin' : 'destination',
        label: dest.label,
        address: dest.label,
        lat: dest.lat,
        lng: dest.lng,
        sortOrder: order,
        linkedOptionId: null,
      });
      order += 1;
    }
  }

  for (const opt of selected) {
    const stop = stopFromOption(opt, order);
    if (!stop) continue;
    const id = doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops')).id;
    batch.set(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops', id), {
      ...stop,
      sortOrder: order,
      linkedOptionId: opt.id,
    });
    order += 1;
  }

  for (const s of manualStops) {
    batch.set(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops', s.id), {
      kind: s.kind,
      label: s.label,
      address: s.address || null,
      lat: s.lat ?? null,
      lng: s.lng ?? null,
      startAt: s.startAt || null,
      endAt: s.endAt || null,
      sortOrder: order,
      linkedOptionId: null,
    });
    order += 1;
  }

  await batch.commit();
}

export function subscribeTripLocations(
  tripId: string,
  onData: (locations: PeopleTravelLocation[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations'),
    snap => {
      const rows = snap.docs.map(d => parsePeopleTravelLocation(d.id, d.data()));
      rows.sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
      onData(rows);
    },
    err => {
      firestoreSnapshotListenError('peopleTravelFirestore', `${PEOPLE_TRAVEL_TRIPS}/${tripId}/locations`, err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export function subscribeTripEvents(
  tripId: string,
  onData: (events: PeopleTravelEvent[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events'),
    snap => {
      onData(snap.docs.map(d => parsePeopleTravelEvent(d.id, d.data())));
    },
    err => {
      firestoreSnapshotListenError('peopleTravelFirestore', `${PEOPLE_TRAVEL_TRIPS}/${tripId}/events`, err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export function subscribeTripLegs(
  tripId: string,
  onData: (legs: PeopleTravelLeg[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs'),
    snap => {
      onData(snap.docs.map(d => parsePeopleTravelLeg(d.id, d.data())));
    },
    err => {
      firestoreSnapshotListenError('peopleTravelFirestore', `${PEOPLE_TRAVEL_TRIPS}/${tripId}/legs`, err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export function parsePeopleTravelPlannerMessage(
  id: string,
  raw: Record<string, unknown>,
): PeopleTravelPlannerMessage {
  const created = raw.createdAt;
  let createdAt = Date.now();
  if (typeof created === 'number' && Number.isFinite(created)) createdAt = created;
  else if (created && typeof created === 'object' && created !== null && 'toDate' in created) {
    createdAt = (created as { toDate: () => Date }).toDate().getTime();
  }
  return {
    id,
    role: raw.role === 'planner' ? 'planner' : 'user',
    text: String(raw.text || ''),
    imageCount: typeof raw.imageCount === 'number' ? raw.imageCount : undefined,
    createdAt,
    createdByUserId: typeof raw.createdByUserId === 'string' ? raw.createdByUserId : undefined,
  };
}

export function subscribeTripPlannerMessages(
  tripId: string,
  onData: (messages: PeopleTravelPlannerMessage[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'plannerMessages'), orderBy('createdAt', 'asc')),
    snap => {
      onData(snap.docs.map(d => parsePeopleTravelPlannerMessage(d.id, d.data())));
    },
    err => {
      firestoreSnapshotListenError(
        'peopleTravelFirestore',
        `${PEOPLE_TRAVEL_TRIPS}/${tripId}/plannerMessages`,
        err,
      );
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

export async function addTripPlannerMessage(
  tripId: string,
  message: Omit<PeopleTravelPlannerMessage, 'createdByUserId'> & { createdByUserId?: string },
): Promise<string> {
  const id = message.id || doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'plannerMessages')).id;
  const uid = message.createdByUserId || auth.currentUser?.uid || '';
  await setDoc(
    doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'plannerMessages', id),
    {
      role: message.role,
      text: message.text,
      imageCount: message.imageCount || 0,
      createdAt: message.createdAt ? new Date(message.createdAt) : serverTimestamp(),
      createdByUserId: uid || null,
    },
    { merge: true },
  );
  return id;
}

export async function upsertTripLocation(
  tripId: string,
  location: Omit<PeopleTravelLocation, 'id'> & { id?: string },
): Promise<string> {
  const id = location.id || doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations')).id;
  await setDoc(
    doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations', id),
    {
      label: location.label,
      kind: location.kind || null,
      address: location.address || null,
      placeId: location.placeId || null,
      lat: location.lat ?? null,
      lng: location.lng ?? null,
      sortOrder: location.sortOrder,
      x: location.x,
      y: location.y,
      startDate: location.startDate || null,
      endDate: location.endDate || null,
    },
    { merge: true },
  );
  return id;
}

export async function deleteTripLocation(tripId: string, locationId: string): Promise<void> {
  const [eventsSnap, legsSnap] = await Promise.all([
    getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events')),
    getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs')),
  ]);
  const batch = writeBatch(db);
  batch.delete(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations', locationId));
  for (const d of eventsSnap.docs) {
    if (d.data().locationId === locationId) batch.delete(d.ref);
  }
  for (const d of legsSnap.docs) {
    const data = d.data();
    if (data.fromLocationId === locationId || data.toLocationId === locationId) {
      batch.delete(d.ref);
    }
  }
  await batch.commit();
}

export async function upsertTripEvent(
  tripId: string,
  event: Omit<PeopleTravelEvent, 'id'> & { id?: string },
): Promise<string> {
  const id = event.id || doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events')).id;
  await setDoc(
    doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events', id),
    {
      locationId: event.locationId,
      title: event.title,
      kind: event.kind,
      startAt: event.startAt || null,
      endAt: event.endAt || null,
      notes: event.notes || null,
      x: event.x,
      y: event.y,
    },
    { merge: true },
  );
  return id;
}

export async function deleteTripEvent(tripId: string, eventId: string): Promise<void> {
  await deleteDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events', eventId));
}

export async function upsertTripLeg(
  tripId: string,
  leg: Omit<PeopleTravelLeg, 'id'> & { id?: string },
): Promise<string> {
  const id = leg.id || doc(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs')).id;
  await setDoc(
    doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs', id),
    {
      fromLocationId: leg.fromLocationId,
      toLocationId: leg.toLocationId,
      mode: leg.mode,
      travelerUserIds: leg.travelerUserIds || [],
      notes: leg.notes || null,
      selectedOptionIds: leg.selectedOptionIds || [],
    },
    { merge: true },
  );
  return id;
}

export async function deleteTripLeg(tripId: string, legId: string): Promise<void> {
  await deleteDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs', legId));
}

export async function writeTripCanvasGraph(
  tripId: string,
  writes: CanvasGraphWrites,
  opts?: { replace?: boolean },
): Promise<void> {
  if (!writes.locations.length && !writes.events.length && !writes.legs.length) return;
  const replace = opts?.replace !== false && writes.locations.length > 0;
  const [locSnap, evSnap, legSnap] = replace
    ? await Promise.all([
        getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations')),
        getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events')),
        getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs')),
      ])
    : [null, null, null];
  const batch = writeBatch(db);
  if (locSnap && evSnap && legSnap) {
    const keepLoc = new Set(writes.locations.map(l => l.id));
    const keepEv = new Set(writes.events.map(e => e.id));
    const keepLeg = new Set(writes.legs.map(l => l.id));
    locSnap.docs.forEach(d => {
      if (!keepLoc.has(d.id)) batch.delete(d.ref);
    });
    evSnap.docs.forEach(d => {
      if (!keepEv.has(d.id)) batch.delete(d.ref);
    });
    legSnap.docs.forEach(d => {
      if (!keepLeg.has(d.id)) batch.delete(d.ref);
    });
  }
  for (const location of writes.locations) {
    batch.set(
      doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations', location.id),
      {
        label: location.label,
        kind: location.kind || null,
        address: location.address || null,
        placeId: location.placeId || null,
        lat: location.lat ?? null,
        lng: location.lng ?? null,
        sortOrder: location.sortOrder,
        x: location.x,
        y: location.y,
        startDate: location.startDate || null,
        endDate: location.endDate || null,
      },
      { merge: true },
    );
  }
  for (const event of writes.events) {
    batch.set(
      doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events', event.id),
      {
        locationId: event.locationId,
        title: event.title,
        kind: event.kind,
        startAt: event.startAt || null,
        endAt: event.endAt || null,
        notes: event.notes || null,
        x: event.x,
        y: event.y,
      },
      { merge: true },
    );
  }
  for (const leg of writes.legs) {
    batch.set(
      doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs', leg.id),
      {
        fromLocationId: leg.fromLocationId,
        toLocationId: leg.toLocationId,
        mode: leg.mode,
        travelerUserIds: leg.travelerUserIds || [],
        notes: leg.notes || null,
        selectedOptionIds: leg.selectedOptionIds || [],
      },
      { merge: true },
    );
  }
  await batch.commit();
}

/** Draw the agent's draft on the board even if Cloud Functions omitted canvas arrays. */
export async function applyTripPlanDraftToCanvas(tripId: string, draft: TripPlanDraft): Promise<boolean> {
  const locSnap = await getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations'));
  const existingLocations = locSnap.docs.map(d => ({
    id: d.id,
    label: String(d.data().label || ''),
  }));
  const writes = canvasWritesFromDraft({ existingLocations, draft });
  if (!writes.locations.length && !writes.events.length && !writes.legs.length) return false;
  await writeTripCanvasGraph(tripId, writes, { replace: writes.locations.length > 0 });
  const [locAfter, evAfter, legAfter] = await Promise.all([
    getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations')),
    getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events')),
    getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs')),
  ]);
  await replacePlanDraftStops(tripId, {
    locations: locAfter.docs.map(d => parsePeopleTravelLocation(d.id, d.data())),
    events: evAfter.docs.map(d => parsePeopleTravelEvent(d.id, d.data())),
    legs: legAfter.docs.map(d => parsePeopleTravelLeg(d.id, d.data())),
  });
  const dests = writes.locations
    .filter(l => l.label.trim().toLowerCase() !== 'home')
    .map(l => ({
      label: l.label,
      ...(typeof l.lat === 'number' ? { lat: l.lat } : {}),
      ...(typeof l.lng === 'number' ? { lng: l.lng } : {}),
    }));
  const patch: Parameters<typeof updatePeopleTravelTrip>[1] = {};
  if (draft.title?.trim()) patch.title = draft.title.trim();
  if (draft.purpose) patch.purpose = draft.purpose;
  if (draft.startDate) patch.startDate = draft.startDate;
  if (draft.endDate) patch.endDate = draft.endDate;
  if (dests.length) patch.destinations = dests;
  if (draft.summary?.trim()) patch.itineraryNotes = draft.summary.trim();
  if (draft.travelerUserIds?.length) patch.travelerUserIds = draft.travelerUserIds;
  if (Object.keys(patch).length) await updatePeopleTravelTrip(tripId, patch);
  return true;
}

async function replacePlanDraftStops(tripId: string, writes: CanvasGraphWrites): Promise<void> {
  const stops = buildStopsFromTripPlanBoard(writes);
  if (!stops.length) return;
  const stopsSnap = await getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops'));
  const batch = writeBatch(db);
  stopsSnap.docs.forEach(d => {
    if (d.data()?.source === 'plan_draft') batch.delete(d.ref);
  });
  stops.forEach(stop => {
    const { id, ...rest } = stop;
    batch.set(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'stops', id), {
      ...rest,
      linkedOptionId: null,
    });
  });
  await batch.commit();
}

/** First open of an older trip: destinations → clusters, selected quotes → legs/events. */
export async function seedTripCanvasIfEmpty(tripId: string): Promise<boolean> {
  const locSnap = await getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations'));
  if (!locSnap.empty) return false;

  const tripSnap = await getDoc(doc(db, PEOPLE_TRAVEL_TRIPS, tripId));
  if (!tripSnap.exists()) return false;
  const trip = parsePeopleTravelTrip(tripSnap.id, tripSnap.data() || {});
  const optSnap = await getDocs(collection(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options'));
  const options = optSnap.docs.map(d => parsePeopleTravelOption(d.id, d.data()));
  const seed = buildTripCanvasSeed(trip, options);
  if (!seed.locations.length) return false;

  const batch = writeBatch(db);
  for (const location of seed.locations) {
    batch.set(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'locations', location.id), {
      label: location.label,
      placeId: location.placeId || null,
      lat: location.lat ?? null,
      lng: location.lng ?? null,
      sortOrder: location.sortOrder,
      x: location.x,
      y: location.y,
      startDate: location.startDate || null,
      endDate: location.endDate || null,
    });
  }
  for (const event of seed.events) {
    batch.set(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'events', event.id), {
      locationId: event.locationId,
      title: event.title,
      kind: event.kind,
      startAt: event.startAt || null,
      endAt: event.endAt || null,
      notes: event.notes || null,
      x: event.x,
      y: event.y,
    });
    const optionId = event.id.replace('seed-event-hotel-', '');
    if (optSnap.docs.some(d => d.id === optionId)) {
      batch.update(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options', optionId), {
        eventId: event.id,
      });
    }
  }
  for (const leg of seed.legs) {
    batch.set(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'legs', leg.id), {
      fromLocationId: leg.fromLocationId,
      toLocationId: leg.toLocationId,
      mode: leg.mode,
      travelerUserIds: [],
      notes: null,
      selectedOptionIds: leg.selectedOptionIds || [],
    });
    for (const optionId of leg.selectedOptionIds || []) {
      batch.update(doc(db, PEOPLE_TRAVEL_TRIPS, tripId, 'options', optionId), {
        legId: leg.id,
      });
    }
  }
  await batch.commit();
  return true;
}
